import React from 'react';
import Head from 'next/head';
import includes from 'lodash/includes';
import moment from 'moment';
import get from 'lodash/get';
import MetaTags from '~/components/MetaTags/MetaTags';
import logEvent from '~/util/analytics';
import Layout from '~/components/Layout/Layout';
import BixbyLayout from '~/components/Bixby/BixbyLayout/BixbyLayout';
import MatchupComponent from '~/components/Matchup/Matchup';
import RelatedNewsRiver from '~/components/RelatedNewsRiver/RelatedNewsRiver';
import NotFound from '~/components/NotFound/NotFound';
import { initStore } from '~/state/store';
import { bindActionCreators } from 'redux';
import { operations as pageDataOperations } from '~/state/pageData';
import { operations as uaOperations } from '~/state/userAgent';
import { operations as eventRecordsOperations } from '~/state/eventRecords';
import { selectMatchup, selectTeamStats } from '~/state/matchup/reducers';
import matchupOperations from '~/state/matchup/operations';
import withState from '~/hocs/withState';
import NCAABMatchup from '~/components/NCAABMatchup/NCAABMatchup';
import MatchupMenu from '~/components/MatchupMenu/MatchupMenu';
import NBAMatchup from '~/components/NBAMatchup/NBAMatchup';
import FootballMatchup from '~/components/FootballMatchup/FootballMatchup';
import MLBMatchup from '~/components/MLBMatchup/MLBMatchup';
import SoccerMatchup from '~/components/SoccerMatchup/SoccerMatchup';
import NHLMatchup from '~/components/NHLMatchup/NHLMatchup';
import LandingOne from '~/components/Bixby/LandingOne/LandingOne';
import ResultsMatchupCard from '~/components/ResultsMatchupCard/ResultsMatchupCard';
import Ad, { AdTypes } from '~/containers/AdContainer/AdContainer';
import ResultsMap from '~/constants/ResultsMap';
import isBixbyUser from '~/util/isBixbyUser';
import getOr from '~/util/getOr';
import css from './matchup.scss';

interface MatchupProps {
  leagueSlug: string;
  event: { data: Event };
  loadRelatedNews: (
    arg: {
      relatedUri: string;
      relatedNews: { before: string; relatedContent: object[] };
    }
  ) => void;
  setAdConfig: (payload: {}, section: string) => void;
  bixbyUser?: boolean;
  url: object;
  isResultsMatchup: boolean;
  eventRecords: any;
  teamStats: any;
}

class MatchupPage extends React.Component<MatchupProps> {
  static displayName = 'MatchupPage';
  static defaultProps = {
    bixbyUser: false,
  };

  static async getInitialProps(props) {
    const bixbyUser = props.isServer ? isBixbyUser(props.req.headers) : false;
    const resultsPageLeagues = ['nascar', 'formula1', 'mma', 'atp', 'wta'];
    const isResultsMatchup = resultsPageLeagues.includes(props.leagueSlug);

    props.store.dispatch(uaOperations.storeBixbyFlag(bixbyUser));

    if (props.query.leagueSlug) {
      if (!parseInt(props.query.eventId, 10)) {
        // eslint-disable-next-line
        props.res.statusCode = 404;
        return { leagueSlug: props.query.leagueSlug };
      }
      const eventResult = await props.store.dispatch(
        matchupOperations.fetchMatchup(props.leagueSlug, props.query.eventId)
      );
      if (eventResult.result === 'FAILURE') {
        // eslint-disable-next-line
        props.res.statusCode = eventResult.statusCode;
      }
      if (isResultsMatchup) {
        const eventNavElements = getOr(
          ResultsMap[props.leagueSlug],
          'matchupCards.eventElements',
          []
        );

        await Promise.all(
          eventNavElements.map(navElement =>
            props.store.dispatch(
              eventRecordsOperations.setEventRecords(
                props.query.leagueSlug,
                props.query.eventId,
                navElement.property
              )
            )
          )
        );
      }
    }
    return {
      bixbyUser,
      isResultsMatchup,
      leagueSlug: props.query.leagueSlug,
    };
  }

  componentDidMount() {
    const event = this.props.event.data;
    if (this.pageDidLoad()) {
      this.props.loadRelatedNews({
        relatedUri: event.resource_uri,
        relatedNews: { before: null, relatedContent: [] },
      });

      this.props.setAdConfig({}, 'matchup');

      logEvent('page_view', {
        league: getOr(event, 'league.slug', ''),
        page_type: 'matchup',
        page_name: getOr(event, 'id', ''),
        match_id: getOr(event, 'id', ''),
        channel: this.props.bixbyUser ? 'bixby' : 'web',
        page_url: getOr(this.props, 'url.asPath', ''),
      });
    }
  }

  getHeading() {
    const { isResultsMatchup } = this.props;

    return isResultsMatchup ? this.getResultsMatchupTitle() : this.getMatchupTitle();
  }

  getMatchupTitle() {
    const event = this.props.event.data;

    const homeTeam = get(event, 'home_team.full_name', 'TBD');
    const awayTeam = get(event, 'away_team.full_name', 'TBD');
    const gameDate = event.game_date ? moment(event.game_date).format('MMMM D, YYYY') : 'TBD';

    return `${awayTeam} @ ${homeTeam} on ${gameDate}`;
  }

  getResultsMatchupTitle() {
    const event = this.props.event.data;

    const tournamentName = get(event, 'name', '');

    return `${tournamentName} Results`;
  }

  pageDidLoad() {
    const event = this.props.event.data;
    return event && event.id && !event.api_uri.includes('/error/events');
  }

  renderMatchup() {
    const event = this.props.event.data;
    if (event.league.sport_name === 'soccer') {
      return <SoccerMatchup event={event} stats={this.props.teamStats} />;
    }

    switch (event.league.slug) {
      case 'nhl':
        return <NHLMatchup event={event} stats={this.props.teamStats} />;
      case 'wnba':
      case 'nba':
        return <NBAMatchup event={event} stats={this.props.teamStats} />;
      case 'cfl':
      case 'nfl':
      case 'ncaaf':
        return <FootballMatchup event={event} stats={this.props.teamStats} />;
      case 'wcbk':
      case 'ncaab':
        return <NCAABMatchup event={event} stats={this.props.teamStats} />;
      case 'mlb':
        return <MLBMatchup event={event} stats={this.props.teamStats} />;
      case 'nascar':
      case 'formula1':
      case 'wta':
      case 'atp':
      case 'mma':
        return (
          <div className={css.resultsMatchupCard}>
            <ResultsMatchupCard event={event} eventRecords={this.props.eventRecords} />
          </div>
        );
      default:
        return <MatchupComponent event={event} />;
    }
  }

  render() {
    const event = this.props.event.data;
    if (!event || !event.id || includes(event.api_uri, '/error/events')) {
      return <NotFound recordNotFound="event" />;
    }

    if (this.props.bixbyUser) {
      return (
        <BixbyLayout>
          <LandingOne leagueSlug={this.props.leagueSlug} section="events" object={event} />
        </BixbyLayout>
      );
    }
    const metaTagType = this.props.isResultsMatchup ? 'results_matchup' : 'matchup';
    return (
      <Layout>
        <Head>
          <style dangerouslySetInnerHTML={{ __html: css.stylesheet }} />
        </Head>
        <MetaTags content={event} type={metaTagType} />
        <Ad leagueSlug={this.props.leagueSlug} adType={AdTypes.LEADERBOARD} />
        {!this.props.isResultsMatchup && (
          <div className={css.matchupMenuContainer}>
            <MatchupMenu event={event} currentSection="matchup" />
          </div>
        )}
        <h1 className="sr-only">{this.getHeading()}</h1>
        {this.renderMatchup()}
        <RelatedNewsRiver resourceUri={event.related_uri} />
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  event: selectMatchup(state),
  eventRecords: state.eventRecordsData.cache,
  teamStats: selectTeamStats(state),
});

const mapDispatchToProps = dispatch => ({
  loadRelatedNews: bindActionCreators(pageDataOperations.loadRelatedNews, dispatch),
  setAdConfig: bindActionCreators(pageDataOperations.setAdConfig, dispatch),
});

export default withState({
  mapStateToProps,
  mapDispatchToProps,
  createStore: initStore,
})(MatchupPage);
